import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-hot-toast";
import { Camera, ImageIcon, Store, ShieldCheck, ShieldAlert, Clock3, Star, TrendingUp, Package2, Gauge } from "lucide-react";

import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";

import { Seller } from "@/services/types/domain";
import sellersApi from "@/services/api/sellers"; // tu servicio real
import { UPLOADS_URL } from "@/utils/utils";


// Extiende Seller con campos adicionales del backend (opcionales)
export type SellerExtra = Seller & {
    performance_metrics?: {
        average_product_rating?: number | null;
        average_fulfillment_time_days?: number | null;
    };
    product_stats?: {
        total_active?: number;
        low_stock_count?: number;
    };
    operational_info?: {
        member_since?: string | null; // ISO/fecha
        last_sale_at?: string | null; // ISO/fecha
    };
};

function normalizeSeller(src: any): SellerExtra {
    const base: Seller = {
        id: Number(src.id),
        user_id: Number(src.user_id),
        store_name: src.store_name ?? "",
        store_description: src.store_description ?? "",
        store_logo: src.store_logo ?? null,
        store_banner: src.store_banner ?? null,
        business_license: src.business_license ?? "",
        tax_id: src.tax_id ?? "",
        business_address: src.business_address ?? "",
        verification_status: src.verification_status || "pending",
        rating_average: typeof src.rating_average === "number" ? src.rating_average : Number(src.rating_average ?? 0),
        total_reviews: typeof src.total_reviews === "number" ? src.total_reviews : Number(src.total_reviews ?? 0),
        total_sales: typeof src.total_sales === "number" ? src.total_sales : Number(src.total_sales ?? 0),
        is_featured: typeof src.is_featured === "boolean" ? src.is_featured : Boolean(Number(src.is_featured ?? 0)),
        commission_rate: typeof src.commission_rate === "number" ? src.commission_rate : Number(src.commission_rate ?? 0),
        created_at: src.created_at ?? "",
        updated_at: src.updated_at ?? "",
        user: src.user ?? (src.username || src.email
            ? {
                id: Number(src.user_id),
                username: src.username || "",
                email: src.email || "",
                first_name: src.first_name || "",
                last_name: src.last_name || "",
            }
            : undefined),
    };
    const extra: SellerExtra = {
        ...base,
        performance_metrics: src.performance_metrics,
        product_stats: src.product_stats,
        operational_info: src.operational_info,
    };
    return extra;
}

// =====================
// Validación del formulario (campos editables)
// =====================
const schema = z.object({
    store_name: z.string().min(2, "El nombre de la tienda es requerido"),
    store_description: z.string().max(1000, "Máximo 1000 caracteres").optional().or(z.literal("")),
    business_address: z.string().max(500).optional().or(z.literal("")),
    business_license: z.string().max(100).optional().or(z.literal("")),
    tax_id: z.string().max(50).optional().or(z.literal("")),
});

export type SellerProfileForm = z.infer<typeof schema>;

const statusToBadge: Record<Seller["verification_status"], { label: string; variant: React.ComponentProps<typeof Badge>["variant"] }> = {
    verified: { label: "Verificada", variant: "success" },
    pending: { label: "En revisión", variant: "warning" },
    rejected: { label: "Rechazada", variant: "destructive" },
};

// =====================
// Safe calls al servicio con nombres EXACTOS + fallback
// =====================
const callGetProfile = async () => {
    const anySvc = sellersApi as any;
    if (typeof anySvc.getSellerProfile === "function") return anySvc.getSellerProfile();
    // fallbacks por si tu servicio usa otro nombre
    return anySvc.getProfile?.() ?? anySvc.profile?.() ?? anySvc.get?.();
};
const callUpdateProfile = async (payload: SellerProfileForm) => {
    const anySvc = sellersApi as any;
    if (typeof anySvc.updateSellerProfile === "function") return anySvc.updateSellerProfile(payload);
    return anySvc.updateProfile?.(payload) ?? anySvc.update?.(payload);
};
const callUploadLogo = async (file: File) => {
    // usa exactamente los métodos de tu servicio seller.ts
    return (sellersApi as any).uploadStoreLogo(file) as Promise<{ logo_url: string }>;
};
const callUploadBanner = async (file: File) => {
    return (sellersApi as any).uploadStoreBanner(file) as Promise<{ banner_url: string }>;
};
/*const callUploadSellerBanner = async (form: FormData) => {
    const anySvc = sellersApi as any;
    if (typeof anySvc.uploadSellerBanner === "function") return anySvc.uploadSellerBanner(form);
    return anySvc.uploadBanner?.(form) ?? anySvc.updateSellerBanner?.(form);
};*/
const callRequestVerification = async () => {
    const anySvc = sellersApi as any;
    if (typeof anySvc.requestSellerVerification === "function") return anySvc.requestSellerVerification();
    return anySvc.requestVerification?.();
};

const SellerProfilePage: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [seller, setSeller] = useState<SellerExtra | null>(null);

    const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<SellerProfileForm>({
        resolver: zodResolver(schema),
    });

    //const badge = useMemo(() => (seller ? statusToBadge[seller.verification_status] : null), [seller]);

    // Cargar perfil
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                const raw = await callGetProfile();
                const payload = (raw?.data?.data ?? raw?.data ?? raw) as any; // ApiResponse | Seller
                const s = normalizeSeller(payload);
                if (!mounted) return;
                setSeller(s);
                reset({
                    store_name: s.store_name || "",
                    store_description: s.store_description || "",
                    business_address: s.business_address || "",
                    business_license: s.business_license || "",
                    tax_id: s.tax_id || "",
                });
            } catch (err: any) {
                toast.error(err?.response?.data?.message || err?.message || "No se pudo cargar el perfil de vendedor");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [reset]);

    // Guardar
    const onSubmit = async (values: SellerProfileForm) => {
        try {
            setSaving(true);
            const raw = await callUpdateProfile(values);
            const payload = (raw?.data?.data ?? raw?.data ?? raw) as any;
            const s = normalizeSeller(payload);
            setSeller(s);
            toast.success("Perfil de vendedor actualizado");
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo actualizar el perfil");
        } finally {
            setSaving(false);
        }
    };

    // Uploads
    const handleUploadLogo = async (file?: File | null) => {
        if (!file) return;
        try {
            // Llama a uploadStoreLogo(file)
            const { logo_url } = await callUploadLogo(file);
            const path_url = logo_url.replace(UPLOADS_URL, "");
            setSeller((prev) => (prev ? { ...prev, store_logo: path_url } : prev));
            toast.success("Logo actualizado");
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo subir el logo");
        }
    };

    const handleUploadBanner = async (file?: File | null) => {
        if (!file) return;
        try {
            // Llama a uploadStoreBanner(file)
            const { banner_url } = await callUploadBanner(file);
            const path_url = banner_url.replace(UPLOADS_URL, "");
            setSeller((prev) => (prev ? { ...prev, store_banner: path_url } : prev));
            toast.success("Banner actualizado");
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo subir el banner");
        }
    };

    const onRequestVerification = async () => {
        try {
            await callRequestVerification();
            toast.success("Solicitud enviada. Nuestro equipo revisará tu tienda.");
            const raw = await callGetProfile();
            const payload = (raw?.data?.data ?? raw?.data ?? raw) as any;
            setSeller(normalizeSeller(payload));
        } catch (err: any) {
            toast.error(err?.response?.data?.message || err?.message || "No se pudo solicitar la verificación");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center bg-gray-50">
                <div className="text-gray-500 animate-pulse">Cargando perfil de vendedor…</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-6">
            <div className="mx-auto w-full max-w-6xl">
                {/* Banner */}
                <div className="relative overflow-hidden rounded-2xl border bg-white shadow-sm">
                    <div className="h-40 sm:h-56 w-full bg-gradient-to-r from-primary-50 to-accent-50">
                        {seller?.store_banner ? (
                            <img src={UPLOADS_URL + (seller.store_banner) || ""} alt="Banner" className="h-full w-full object-cover" />
                        ) : (
                            <div className="h-full w-full grid place-items-center text-gray-400"><ImageIcon className="w-6 h-6" /> Banner</div>
                        )}
                    </div>
                    <div className="absolute top-3 right-3">
                        <label className="inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-sm font-medium shadow cursor-pointer hover:bg-white">
                            <Camera className="w-4 h-4 text-gray-600" /> Cambiar banner
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadBanner(e.target.files?.[0])} />
                        </label>
                    </div>

                    {/* Header tienda */}
                    <div className="px-4 pb-4 sm:px-6">
                        <div className="-mt-10 flex flex-col sm:flex-row sm:items-end gap-4">
                            <div className="relative h-20 w-20 sm:h-24 sm:w-24 rounded-xl ring-4 ring-white overflow-hidden bg-white grid place-items-center">
                                {seller?.store_logo ? (
                                    <img src={UPLOADS_URL + (seller.store_logo) || ""} alt="Logo" className="h-full w-full object-cover" />
                                ) : (
                                    <Store className="w-8 h-8 text-gray-400" />
                                )}
                                <label className="absolute bottom-1 right-1 grid h-7 w-7 place-items-center rounded-full bg-white shadow cursor-pointer">
                                    <Camera className="w-4 h-4 text-gray-700" />
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadLogo(e.target.files?.[0])} />
                                </label>
                            </div>

                            <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{seller?.store_name || "Mi tienda"}</h1>
                                    {seller && (
                                        <Badge variant={statusToBadge[seller.verification_status].variant}>
                                            {seller.verification_status === "verified" ? <ShieldCheck className="mr-1 h-3.5 w-3.5" /> : seller.verification_status === "rejected" ? <ShieldAlert className="mr-1 h-3.5 w-3.5" /> : null}
                                            {statusToBadge[seller.verification_status].label}
                                        </Badge>
                                    )}
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                                    <span className="inline-flex items-center gap-1"><Star className="w-4 h-4" /> {Number(seller?.rating_average ?? 0).toFixed(2)} ({seller?.total_reviews ?? 0} reseñas)</span>
                                    <span className="inline-flex items-center gap-1"><TrendingUp className="w-4 h-4" /> {seller?.total_sales ?? 0} ventas</span>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <Button variant="outline" onClick={onRequestVerification} disabled={seller?.verification_status != "pending"}>
                                    {seller?.verification_status === "pending" ? "Solicitud enviada" : "Solicitar verificación"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Formulario */}
                <form onSubmit={handleSubmit(onSubmit)} className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Columna principal */}
                    <div className="lg:col-span-2 space-y-6">
                        <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="mb-4 text-lg font-semibold text-gray-900">Información de la tienda</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <Input label="Nombre de la tienda" {...register("store_name")} error={errors.store_name?.message} fullWidth />
                                <Input label="RUC / Tax ID" {...register("tax_id")} error={errors.tax_id?.message} placeholder="Ej. 20XXXXXXXXXX" fullWidth />
                                <Input label="Licencia comercial" {...register("business_license")} error={errors.business_license?.message} placeholder="Nº de licencia (opcional)" fullWidth />
                            </div>
                            <div className="mt-4">
                                <label className="mb-1 block text-sm font-medium text-gray-700">Descripción</label>
                                <textarea
                                    {...register("store_description")}
                                    rows={4}
                                    placeholder="Cuenta a tus clientes sobre tus productos, tiempos de preparación, etc."
                                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                                />
                                {errors.store_description?.message && (
                                    <p className="mt-1 text-xs text-red-600">{errors.store_description?.message}</p>
                                )}
                            </div>
                        </div>

                        <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="mb-2 text-lg font-semibold text-gray-900">Dirección comercial</div>
                            <p className="text-xs text-gray-500 mb-3">Dirección fiscal o de atención al cliente (texto libre).</p>
                            <textarea
                                {...register("business_address")}
                                rows={3}
                                placeholder="Ej. Av. Principal 123, Lima, Perú"
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                            {errors.business_address?.message && (
                                <p className="mt-1 text-xs text-red-600">{errors.business_address?.message}</p>
                            )}
                        </div>
                    </div>

                    {/* Columna lateral */}
                    <div className="space-y-6">
                        {/* Estado/métricas */}
                        <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="mb-4 text-lg font-semibold text-gray-900">Estado y métricas</div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div className="rounded-lg border p-3">
                                    <div className="text-xs text-gray-500">Comisión</div>
                                    <div className="font-semibold">{(seller?.commission_rate ?? 0)}%</div>
                                </div>
                                <div className="rounded-lg border p-3">
                                    <div className="text-xs text-gray-500">Destacado</div>
                                    <div className="font-semibold">{seller?.is_featured ? "Sí" : "No"}</div>
                                </div>
                                <div className="rounded-lg border p-3">
                                    <div className="text-xs text-gray-500">Creado</div>
                                    <div className="font-semibold inline-flex items-center gap-1"><Clock3 className="w-4 h-4" /> {seller?.created_at ? new Date(seller.created_at).toLocaleDateString() : "—"}</div>
                                </div>
                                <div className="rounded-lg border p-3">
                                    <div className="text-xs text-gray-500">Actualizado</div>
                                    <div className="font-semibold inline-flex items-center gap-1"><Clock3 className="w-4 h-4" /> {seller?.updated_at ? new Date(seller.updated_at).toLocaleDateString() : "—"}</div>
                                </div>
                            </div>
                        </div>

                        {/* Nuevos paneles: Productos y Rendimiento */}
                        <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="mb-3 text-lg font-semibold text-gray-900">Productos</div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div className="rounded-lg border p-3 flex items-center gap-2"><Package2 className="w-4 h-4" /> Activos <span className="ml-auto font-semibold">{seller?.product_stats?.total_active ?? 0}</span></div>
                                <div className="rounded-lg border p-3 flex items-center gap-2"><Gauge className="w-4 h-4" /> Bajo stock <span className="ml-auto font-semibold">{seller?.product_stats?.low_stock_count ?? 0}</span></div>
                            </div>
                        </div>

                        <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="mb-3 text-lg font-semibold text-gray-900">Rendimiento y operaciones</div>
                            <div className="space-y-2 text-sm text-gray-700">
                                <div className="flex items-center justify-between">
                                    <span>Rating promedio de productos</span>
                                    <span className="font-semibold">{seller?.performance_metrics?.average_product_rating ?? "—"}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span>Tiempo promedio de fulfilment (días)</span>
                                    <span className="font-semibold">{seller?.performance_metrics?.average_fulfillment_time_days ?? "—"}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span>Miembro desde</span>
                                    <span className="font-semibold">{seller?.operational_info?.member_since ? new Date(seller.operational_info.member_since).toLocaleDateString() : "—"}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span>Última venta</span>
                                    <span className="font-semibold">{seller?.operational_info?.last_sale_at ? new Date(seller.operational_info.last_sale_at).toLocaleString() : "—"}</span>
                                </div>
                            </div>
                        </div>

                        {/* Guardar */}
                        <div className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <Button type="submit" loading={saving || isSubmitting} fullWidth>
                                Guardar cambios
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SellerProfilePage;
